import React, { useEffect } from 'react'
import { useHistory } from 'react-router'
import { useForm, Controller } from 'react-hook-form-7'
import omit from 'lodash/omit'
import { Checkbox } from '@retailer-platform/shared-components/src/common/checkbox/Checkbox'
import {
  CenteredContent,
  Section,
  PageLayout,
  FormField,
  PageLayoutFooter,
  Center,
  Alert,
  Text,
  spacing,
} from '@retailer-platform/shared-components'
import { AccountManagementRetailerLocationStoreGroupSelect } from 'domains/account-management/components/retailer-location-store-group-select/AccountManagementRetailerLocationStoreGroupSelect'
import { AccountManagementRetailerSelect } from 'domains/account-management/components/retailer-select/AccountManagementRetailerSelect'
import { AccountManagementRoleSelect } from 'domains/account-management/components/role-select/AccountManagementRoleSelect'
import { RoleRank } from 'domains/account-management/components/role-select/utils/AccountManagementRoleSelect.types'
import { AccountManagementSecondaryRoleSelect } from 'domains/account-management/components/role-select/AccountManagementSecondaryRoleSelect'
import { AccountManagementPartnerSelect } from 'domains/account-management/components/partner-select/AccountManagementPartnerSelect'
import { emailValidationPattern } from 'utils/validators/emailValidation'
import { AccountFormInput } from 'domains/account-management/components/account-form/components/form-input/AccountFormInput'
import {
  useDomainMessages,
  type DomainMessages,
} from 'domains/account-management/utils/domain/intl'
import LoadingDots from 'legacy/components/LoadingDots'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import {
  type FormData,
  type AccountFormProps,
  RetailerTerminology,
} from './utils/AccountForm.types'
import {
  StyledButton,
  StyledLoadingDots,
  sectionStyles,
  FieldRow,
  FormFieldBottom,
  StyledAccountForm,
  StyledPageLayoutContent,
  FormActionsFooter,
  FullAccessSection,
  FormFieldLabel,
  CreateAnotherContainer,
} from './utils/AccountForm.styles'
import { useAccountForm } from './utils/useAccountForm.hooks'

export const emptyInitialState: Partial<FormData> = {
  givenName: '',
  surname: '',
  email: '',
  title: '',
  roles: [],
  partners: [],
  retailers: [],
  retailerLocations: [],
  retailerLocationsStoreGroups: [],
  hasAllPartnersFullAccess: false,
  hasFullPartnerAccess: false,
  hasFullRetailerAccess: false,
  createAnother: false,
}

export const accountManagementFormTestIds = {
  allPartnersFullAccessSection: 'account-management-form-all-partners',
  allPartnersFullAccessCheckbox: 'account-management-form-all-partners-checkbox',
  fullPartnersAccessCheckbox: 'account-management-form-full-partners-checkbox',
  fullRetailerAccessCheckbox: 'account-management-form-full-retailer-checkbox',
  submitButton: 'account-management-form-submit',
  cancel: 'account-management-form-cancel',
  loading: 'account-management-form-loading',
  authServiceRequiredInfo: 'account-management-auth-service-required-info',
  hasFullPartnerAccessInfo: 'account-management-full-partner-access-info',
  hasFullRetailerAccessInfo: 'account-management-full-retailer-access-info',
  partnerSelectError: 'account-management-partner-select-error',
  retailerSelectError: 'account-management-retailer-select-error',
  retailerLocationSelectError: 'account-management-retailer-location-select-error',
  rolesSelectError: 'account-management-roles-select-error',
  retailerLocationsStoreGroupsError:
    'account-management-retailer-location-store-group-select-error',
  retailerSelectSection: 'account-management-retailer-select-section',
  retailerLocationSelectSection: 'account-management-retailer-location-select-section',
  createAnotherCheckbox: 'account-management-create-another-checkbox',
}

export const AccountManagementAccountForm: React.FunctionComponent<AccountFormProps> = ({
  showAllPartnersFullAccessToggle,
  retailerTerminology,
  initialState = emptyInitialState,
  onFormSubmit,
  formLoading,
  SuccessNotification,
  ErrorNotification,
  disableFields = {},
  allowAllPartnersFullAccess,
  allowFullPartnerAccess,
  allowFullRetailerAccess,
  allowCreateAnother = false,
  partnerId,
  isUpdate,
  isCurrentAccountReadOnly,
}) => {
  const { notify, notifyError } = useNotifications()
  const form = useForm<FormData>({
    defaultValues: initialState,
    mode: 'onChange',
  })
  const { register, control, handleSubmit, formState, trigger } = form
  const { errors, isValid } = formState
  const history = useHistory()

  const {
    canSelectPartners,
    canSelectRetailerLocations,
    canSelectRetailers,
    hasAllPartnersFullAccess,
    partnerIds,
    retailerIds,
  } = useAccountForm(form)

  const allowMultiPartnerAssignment = showAllPartnersFullAccessToggle

  const onSubmit = (formData: FormData) => {
    onFormSubmit(formData, form)
      .then(() => notify(<SuccessNotification />, 'modern'))
      .catch(() => notifyError(<ErrorNotification />, 'modern'))
  }

  const defaultErrorMessages: Record<string, DomainMessages> = {
    required: 'accountManagementDomain.validation.required',
  }

  const i18n = useDomainMessages({
    givenName: 'accountManagementDomain.form.fields.givenName',
    lastName: 'accountManagementDomain.form.fields.surname',
    email: 'accountManagementDomain.form.fields.email',
    title: 'accountManagementDomain.form.fields.title',
    role: 'accountManagementDomain.form.fields.role',
    authServiceRequiredMessage: 'accountManagementDomain.form.info.authServiceRequired',
    hasFullPartnerAccessMessage: 'accountManagementDomain.form.info.hasFullPartnerAccess',
    hasFullRetailerAccessMessage: 'accountManagementDomain.form.info.hasFullRetailerAccess',
    fullPartnerAccess:
      retailerTerminology == RetailerTerminology.InternalTerms
        ? 'accountManagementDomain.internalTerms.form.fields.fullPartnerAccess'
        : 'accountManagementDomain.retailerTerms.form.fields.fullPartnerAccess',
    fullRetailerAccess: 'accountManagementDomain.form.fields.fullRetailerAccess',
    allPartnersFullAccessMessage: 'accountManagementDomain.form.fields.hasAllPartnersFullAccess',
    submitButtonMessage: 'accountManagementDomain.form.submitButton',
    cancelButtonMessage: 'accountManagementDomain.form.cancelButton',
    partnersPlaceholderMessage:
      retailerTerminology == RetailerTerminology.InternalTerms
        ? 'accountManagementDomain.internalTerms.partner.placeholder'
        : 'accountManagementDomain.retailerTerms.partner.placeholder',
    partnersLoadingMessage:
      retailerTerminology == RetailerTerminology.InternalTerms
        ? 'accountManagementDomain.internalTerms.partners.loading'
        : 'accountManagementDomain.retailerTerms.partners.loading',
    retailersPlaceholderMessage:
      retailerTerminology == RetailerTerminology.InternalTerms
        ? 'accountManagementDomain.internalTerms.retailer.placeholder'
        : 'accountManagementDomain.retailerTerms.retailer.placeholder',
    retailersLoadingMessage:
      retailerTerminology == RetailerTerminology.InternalTerms
        ? 'accountManagementDomain.internalTerms.retailers.loading'
        : 'accountManagementDomain.retailerTerms.retailers.loading',
    storeSectionTitle: 'accountManagementDomain.form.section.store',
    accountSectionTitle: 'accountManagementDomain.form.section.account',
    retailerSelectLabel: 'accountManagementDomain.form.fields.retailerLabel',
    bannerLabel: 'accountManagementDomain.form.fields.bannerLabel',
    createAnotherLabel: 'accountManagementDomain.form.fields.createAnotherLabel',
  })

  const errorMessages = useDomainMessages({
    partners: 'accountManagementDomain.validation.partners',
    retailers: 'accountManagementDomain.validation.retailers',
    retailerLocations: 'accountManagementDomain.validation.retailerLocations',
    roles: 'accountManagementDomain.validation.roles',
  })

  useEffect(() => {
    // if the form is launching in edit mode trigger a validate to highlight any fields with
    // errors. Typical this is a missing last name in the data.
    if (isUpdate) {
      trigger()
    }
  }, [isUpdate, trigger])

  if (isCurrentAccountReadOnly) {
    return <ReadOnlyAccountInfo />
  }

  const submitDisabled = formLoading || !isValid
  const partnersDisabled = formLoading || !canSelectPartners
  const retailersDisabled = formLoading || !canSelectRetailers
  const retailersFullAccessCheckBoxDisabled =
    formLoading || !canSelectPartners || partnerIds.length === 0
  const retailerLocationsDisabled = formLoading || !canSelectRetailerLocations
  const retailerLocationsFullAccessCheckboxDisabled =
    formLoading || !canSelectRetailers || retailerIds.length === 0

  return (
    <StyledAccountForm onSubmit={handleSubmit(onSubmit)}>
      <PageLayout noHeader noSidebar>
        <StyledPageLayoutContent>
          <CenteredContent>
            <Section title={i18n.storeSectionTitle} bodyStyles={sectionStyles}>
              <FormField>
                {showAllPartnersFullAccessToggle && allowAllPartnersFullAccess && (
                  <FullAccessSection>
                    <Controller
                      render={({ field }) => (
                        <Checkbox
                          id="hasAllPartnersFullAccess"
                          disabled={formLoading}
                          checked={field.value}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          name={field.name}
                          data-testid={accountManagementFormTestIds.allPartnersFullAccessCheckbox}
                        />
                      )}
                      name="hasAllPartnersFullAccess"
                      control={control}
                    />
                    <label htmlFor="hasAllPartnersFullAccess">
                      {i18n.allPartnersFullAccessMessage}
                    </label>
                  </FullAccessSection>
                )}
                <FormFieldLabel disabled={partnersDisabled} htmlFor="partners">
                  {i18n.retailerSelectLabel}
                </FormFieldLabel>
                <Controller
                  control={control}
                  name="partners"
                  rules={{
                    validate: value =>
                      !canSelectPartners || (Array.isArray(value) && value.length > 0),
                  }}
                  render={({ field }) => (
                    <AccountManagementPartnerSelect
                      form={form}
                      {...omit(field, 'ref')}
                      placeholder={i18n.partnersPlaceholderMessage}
                      loadingMessage={i18n.partnersLoadingMessage}
                      allowMultiSelect={allowMultiPartnerAssignment}
                      partnerIdContext={partnerId}
                      isDisabled={partnersDisabled}
                      isUpdate={isUpdate}
                    />
                  )}
                />
                {errors?.partners && (
                  <ErrorMessage data-testid={accountManagementFormTestIds.partnerSelectError}>
                    {errorMessages.partners}
                  </ErrorMessage>
                )}
              </FormField>

              <FormField data-testid={accountManagementFormTestIds.retailerSelectSection}>
                {allowFullPartnerAccess && (
                  <FullAccessSection disabled={retailersFullAccessCheckBoxDisabled}>
                    <Controller
                      render={({ field }) => (
                        <Checkbox
                          id="hasFullPartnerAccess"
                          disabled={retailersFullAccessCheckBoxDisabled}
                          checked={field.value}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          name={field.name}
                          data-testid={accountManagementFormTestIds.fullPartnersAccessCheckbox}
                        />
                      )}
                      name="hasFullPartnerAccess"
                      control={control}
                    />
                    <label htmlFor="hasFullPartnerAccess">{i18n.fullPartnerAccess}</label>
                  </FullAccessSection>
                )}
                <FormFieldLabel disabled={retailersDisabled} htmlFor="retailers">
                  {i18n.bannerLabel}
                </FormFieldLabel>
                <Controller
                  control={control}
                  name="retailers"
                  rules={{
                    validate: value =>
                      !canSelectRetailers ||
                      (allowAllPartnersFullAccess && hasAllPartnersFullAccess) ||
                      (Array.isArray(value) && value.length > 0),
                  }}
                  render={({ field }) => (
                    <AccountManagementRetailerSelect
                      {...omit(field, 'ref')}
                      partnerIds={partnerIds}
                      placeholder={i18n.retailersPlaceholderMessage}
                      loadingMessage={i18n.retailersLoadingMessage}
                      isDisabled={retailersDisabled}
                      isUpdate={isUpdate}
                    />
                  )}
                />
                {errors?.retailers && (
                  <ErrorMessage data-testid={accountManagementFormTestIds.retailerSelectError}>
                    {errorMessages.retailers}
                  </ErrorMessage>
                )}
              </FormField>

              <FormFieldBottom
                data-testid={accountManagementFormTestIds.retailerLocationSelectSection}
              >
                {allowFullRetailerAccess && (
                  <FullAccessSection disabled={retailerLocationsFullAccessCheckboxDisabled}>
                    <Controller
                      render={({ field }) => (
                        <Checkbox
                          id="hasFullRetailerAccess"
                          disabled={retailerLocationsFullAccessCheckboxDisabled}
                          checked={field.value}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          name={field.name}
                          data-testid={accountManagementFormTestIds.fullRetailerAccessCheckbox}
                        />
                      )}
                      name="hasFullRetailerAccess"
                      control={control}
                    />
                    <label htmlFor="hasFullRetailerAccess">{i18n.fullRetailerAccess}</label>
                  </FullAccessSection>
                )}
                <Controller
                  control={control}
                  name="retailerLocationsStoreGroups"
                  rules={{
                    validate: value =>
                      !canSelectRetailerLocations ||
                      (allowAllPartnersFullAccess && hasAllPartnersFullAccess) ||
                      (Array.isArray(value) && value.length > 0),
                  }}
                  render={({ field }) => (
                    <AccountManagementRetailerLocationStoreGroupSelect
                      {...omit(field, 'ref')}
                      retailerIds={retailerIds}
                      isDisabled={retailerLocationsDisabled}
                    />
                  )}
                />
                {errors?.retailerLocationsStoreGroups && (
                  <ErrorMessage
                    data-testid={accountManagementFormTestIds.retailerLocationsStoreGroupsError}
                  >
                    {errorMessages.retailerLocations}
                  </ErrorMessage>
                )}
              </FormFieldBottom>
            </Section>
          </CenteredContent>
          <CenteredContent>
            <Section title={i18n.accountSectionTitle} bodyStyles={sectionStyles}>
              {/* Auth Service stores names as one fullname field. Piggy back on given name for now */}
              <FieldRow>
                <FormField>
                  <FormFieldLabel htmlFor="givenName">{i18n.givenName}</FormFieldLabel>
                  <AccountFormInput
                    labelMessageId="accountManagementDomain.form.fields.givenName"
                    {...register('givenName', { required: true })}
                    errors={errors}
                    errorMessages={defaultErrorMessages}
                    disabled={formLoading}
                    useNewStyle
                  />
                </FormField>
              </FieldRow>
              <AccountFormInput
                hidden
                labelMessageId="accountManagementDomain.form.fields.surname"
                {...register('surname')}
                errors={errors}
                errorMessages={defaultErrorMessages}
                disabled={formLoading}
                useNewStyle
              />
              <FormField>
                <FormFieldLabel htmlFor="email">{i18n.email}</FormFieldLabel>
                <AccountFormInput
                  labelMessageId="accountManagementDomain.form.fields.email"
                  {...register('email', { required: true, pattern: emailValidationPattern })}
                  errors={errors}
                  errorMessages={{
                    ...defaultErrorMessages,
                    pattern: 'accountManagementDomain.validation.email',
                  }}
                  disabled={disableFields.email || formLoading}
                  useNewStyle
                />
              </FormField>

              <FieldRow>
                <FormField>
                  <FormFieldLabel htmlFor="title">{i18n.title}</FormFieldLabel>
                  <AccountFormInput
                    labelMessageId="accountManagementDomain.form.fields.title"
                    {...register('title')}
                    errors={errors}
                    errorMessages={defaultErrorMessages}
                    disabled={formLoading}
                    useNewStyle
                  />
                </FormField>

                <FormField>
                  <FormFieldLabel htmlFor="roles">{i18n.role}</FormFieldLabel>
                  <Controller
                    control={control}
                    name="roles"
                    rules={{
                      validate: value =>
                        Array.isArray(value) &&
                        value.some(role => role.roleRank === RoleRank.Primary),
                    }}
                    render={({ field }) => (
                      <AccountManagementRoleSelect
                        form={form}
                        roleError={errors?.roles}
                        {...omit(field, 'ref')}
                        isDisabled={formLoading}
                        showSecondaryRoles={false}
                      />
                    )}
                  />
                </FormField>
              </FieldRow>

              <FormFieldBottom>
                <Controller
                  control={control}
                  name="roles"
                  rules={{
                    validate: value =>
                      Array.isArray(value) &&
                      value.some(role => role.roleRank === RoleRank.Primary),
                  }}
                  render={({ field }) => (
                    <AccountManagementSecondaryRoleSelect
                      form={form}
                      roleError={errors?.roles}
                      {...omit(field, 'ref')}
                      isDisabled={formLoading}
                    />
                  )}
                />
              </FormFieldBottom>
            </Section>
          </CenteredContent>
        </StyledPageLayoutContent>
        <PageLayoutFooter css={{ height: 64 }}>
          <FormActionsFooter>
            {allowCreateAnother && (
              <CreateAnotherContainer disabled={submitDisabled}>
                <Controller
                  render={({ field }) => (
                    <Checkbox
                      id="createAnother"
                      disabled={submitDisabled}
                      checked={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      name={field.name}
                      data-testid={accountManagementFormTestIds.createAnotherCheckbox}
                    />
                  )}
                  name="createAnother"
                  control={control}
                />
                <label htmlFor="createAnother">{i18n.createAnotherLabel}</label>
              </CreateAnotherContainer>
            )}
            <StyledButton
              type="button"
              variant="tertiary"
              disabled={formLoading}
              data-testid={accountManagementFormTestIds.cancel}
              onClick={history.goBack}
            >
              <span className="button-label">{i18n.cancelButtonMessage}</span>
            </StyledButton>
            <StyledButton
              type="submit"
              variant="primary"
              disabled={submitDisabled}
              data-testid={accountManagementFormTestIds.submitButton}
            >
              <span className="button-label">{i18n.submitButtonMessage}</span>
              {formLoading && (
                <StyledLoadingDots data-testid={accountManagementFormTestIds.loading}>
                  <LoadingDots centered fill="white" height={5} />
                </StyledLoadingDots>
              )}
            </StyledButton>
          </FormActionsFooter>
        </PageLayoutFooter>
      </PageLayout>
    </StyledAccountForm>
  )
}

const ReadOnlyAccountInfo = () => {
  const i18n = useDomainMessages({
    readOnlyAccountInfo: 'accountManagementDomain.form.readOnlyAccountInfo',
  })

  return (
    <Center style={{ maxWidth: 550, padding: spacing.X24 }}>
      <Alert variant="info">
        <Text>{i18n.readOnlyAccountInfo}</Text>
      </Alert>
    </Center>
  )
}
